import React, { memo, useState } from 'react'

// Extraído literalmente de ChartPage.jsx ("HTML signal labels" inline JSX)
// para que ChartPublicPage.jsx pueda mostrar los labels flotantes de los
// indicadores del registro (Entry/SL/TP de breakout_pattern,
// liquidity_sweep_pro, etc.) sin reimplementar el marcado. Puramente
// presentacional: recibe la lista ya posicionada en píxeles (ver
// updateLabelPositions en cada página, que sigue viviendo en la página
// porque depende de refs propios del chart de esa página).
//
// Detalle al click (rango_quantum, validación en pantalla de Marc,
// 2026-08-15): un label opta a ser clicable llevando un campo `detail`
// (fecha apertura, entrada, TP1/TP2, SL/liquidación, estado). El resto de
// indicadores que comparten boxStyle:'flag' nunca ponen `detail`, así que
// sus etiquetas siguen siendo pointer-events-none exactamente como antes —
// cambio aditivo, no toca el comportamiento de nadie más.
function SignalDetailCard({ detail }) {
  return (
    <div
      className="mt-1 rounded shadow-lg text-[10px] leading-tight whitespace-nowrap"
      style={{ background: 'var(--paper, #FDFDFA)', border: '1px solid var(--hair, rgba(176,141,87,0.35))', color: 'var(--ink, #15211B)', padding: '6px 8px' }}
      data-testid="signal-detail-card"
    >
      <div className="flex justify-between gap-3"><span className="opacity-60">Apertura</span><span>{detail.openedAt}</span></div>
      <div className="flex justify-between gap-3"><span className="opacity-60">Entrada</span><span>{detail.entryPrice}</span></div>
      <div className="flex justify-between gap-3"><span className="opacity-60">TP1</span><span>{detail.tp1}</span></div>
      <div className="flex justify-between gap-3"><span className="opacity-60">TP2</span><span>{detail.tp2}</span></div>
      <div className="flex justify-between gap-3"><span className="opacity-60">{detail.stopLabel}</span><span>{detail.stopPrice}</span></div>
      <div className="flex justify-between gap-3"><span className="opacity-60">Estado</span><span>{detail.status}</span></div>
      {detail.closedAt && <div className="flex justify-between gap-3"><span className="opacity-60">Cierre</span><span>{detail.closedAt}</span></div>}
    </div>
  )
}

// Chip de clase A/A+ (WP A/A+ fase 2) -- opt-in vía `label.signalClass`
// ({ cls: 'A+'|'A', tooltip }); hoy solo lo pone el renderer de NASDAQ
// Quantum (utils/signalClass.js::signalClassBadge). Solo el chip recibe
// pointer-events (tooltip nativo `title`): el resto de la etiqueta y toda
// etiqueta sin el campo se comportan exactamente igual que antes. La clase
// solo ETIQUETA: A y A+ se pintan siempre. Etiqueta flag = texto blanco
// sobre el color → A+ relleno blanco, A solo contorno.
function SignalClassChip({ signalClass, color }) {
  const style = signalClass.cls === 'A+'
    ? { backgroundColor: '#FFFFFF', color }
    : { backgroundColor: 'transparent', color: '#FFFFFF', boxShadow: 'inset 0 0 0 1px #FFFFFF' }
  return (
    <span
      className="pointer-events-auto cursor-help ml-1 px-[4px] rounded-[2px] text-[10px] md:text-[8px] font-extrabold leading-[13px] md:leading-[11px]"
      style={style}
      title={signalClass.tooltip}
      data-testid="signal-class-chip"
      data-signal-class={signalClass.cls}
    >
      {signalClass.cls}
    </span>
  )
}

function IndicatorOverlayLabels({ labels }) {
  const [expandedKey, setExpandedKey] = useState(null)

  return labels.map((label, i) => {
    // Identidad estable (indicador+vela), no el índice `i`: `labels` se
    // reconstruye entero en cada recálculo (poll/tick) y el orden dentro del
    // array combinado de todos los indicadores no está garantizado — usar
    // `i` como key de expansión podría dejar el detalle abierto sobre una
    // señal distinta a la que el usuario clicó, o cerrarlo solo, tras el
    // siguiente recálculo.
    const detailKey = label.detail ? `${label.__indicatorId}:${label.time}` : null
    // ── Placa estilo Quantum/Rolex (usada por SuperTrend Quantum) ─────────────
    if ((label.boxStyle === 'plaque' || label.boxStyle === 'cluster') && !label.leftAnchored) {
      const isBull = label.isBull
      const color = label.color || (isBull ? '#35A07A' : '#B4574B')
      return (
        <div
          key={i}
          className="absolute pointer-events-none select-none z-10 flex flex-col items-center"
          style={{
            left: label.x,
            top: isBull ? label.y + (label.offsetY ?? 8) : label.y - (label.offsetY ?? 8),
            transform: 'translateX(-50%)',
          }}
        >
          {isBull && (
            <div
              style={{
                width: 0,
                height: 0,
                borderLeft: '4px solid transparent',
                borderRight: '4px solid transparent',
                borderBottom: `4px solid ${color}`,
                opacity: 0.6,
              }}
            />
          )}
          <div
            className="whitespace-nowrap text-[11px] leading-[15px] font-semibold px-1.5 py-0.5 rounded"
            style={{
              backgroundColor: label.bgColor || `${color}1F`,
              border: `1px solid ${color}`,
              color,
              fontFamily: 'Inter, system-ui, sans-serif',
              boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
            }}
          >
            {label.text}
          </div>
          {!isBull && (
            <div
              style={{
                width: 0,
                height: 0,
                borderLeft: '4px solid transparent',
                borderRight: '4px solid transparent',
                borderTop: `4px solid ${color}`,
                opacity: 0.6,
              }}
            />
          )}
        </div>
      )
    }

    // ── Etiqueta plana (sin fondo/píldora) — estilo usado por ICT Quantum ──────
    if (label.boxStyle === 'plain') {
      const isBull = label.isBull
      const color = label.color || (isBull ? '#35A07A' : '#B4574B')
      return (
        <div
          key={i}
          className="absolute pointer-events-none select-none z-10 text-[11px] md:text-[9px] font-bold whitespace-nowrap leading-[13px]"
          style={{
            left: label.x,
            top: label.y,
            color,
            transform: label.leftAnchored
              ? 'translateY(-50%)'
              : (isBull ? 'translateX(-50%) translateY(4px)' : 'translate(-50%, calc(-100% - 4px))'),
            textShadow: '0 1px 2px rgba(0,0,0,0.75)',
          }}
        >
          {label.text}
        </div>
      )
    }

    return label.boxStyle === 'flag' || label.leftAnchored ? (
      // Etiqueta flotante de nivel (Entry/SL/TP…): caja tipo "flag" con un
      // pico apuntando a la izquierda, anclado exactamente sobre la línea
      // del nivel — estilo label.style_label_left de Pine.
      <div key={i} className="absolute select-none z-10" style={{ left: label.x, top: label.y }}>
        <div
          className={`flex items-center ${label.detail ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none'}`}
          style={{ transform: 'translateY(-50%)' }}
          onClick={label.detail ? () => setExpandedKey((k) => (k === detailKey ? null : detailKey)) : undefined}
          data-testid={label.detail ? 'signal-label' : (label.edgeClamp ? 'edge-level-marker' : undefined)}
        >
          <div style={{ width: 0, height: 0, borderTop: '5px solid transparent', borderBottom: '5px solid transparent', borderRight: `5px solid ${label.color}`, flexShrink: 0 }} />
          <div style={{ backgroundColor: label.color }} className="text-white text-[11px] md:text-[9px] font-bold px-[7px] py-[3px] md:px-[6px] md:py-[2px] rounded-r-[2px] leading-[15px] md:leading-[13px] tracking-wide whitespace-nowrap shadow-sm">
            {label.text}
            {label.signalClass && <SignalClassChip signalClass={label.signalClass} color={label.color} />}
          </div>
        </div>
        {label.detail && expandedKey === detailKey && <SignalDetailCard detail={label.detail} />}
      </div>
    ) : (
      <div
        key={i}
        className="absolute pointer-events-none select-none z-10"
        style={{
          left: label.x,
          top:  label.isBull ? label.y + (label.offsetY ?? 6) : label.y - (label.offsetY ?? 6),
          transform: label.isBull ? 'translateX(-50%)' : 'translate(-50%, -100%)',
        }}
      >
        {label.isBull ? (
          <div className="flex flex-col items-center" style={{ gap: 0 }}>
            <div style={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `5px solid ${label.color}` }} />
            <div style={{ backgroundColor: label.color }} className="text-white text-[11px] md:text-[9px] font-bold px-[6px] md:px-[5px] py-[2px] md:py-px rounded-[2px] leading-[15px] md:leading-[14px] tracking-wide whitespace-nowrap shadow-sm">
              {label.text}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center" style={{ gap: 0 }}>
            <div style={{ backgroundColor: label.color }} className="text-white text-[11px] md:text-[9px] font-bold px-[6px] md:px-[5px] py-[2px] md:py-px rounded-[2px] leading-[15px] md:leading-[14px] tracking-wide whitespace-nowrap shadow-sm">
              {label.text}
            </div>
            <div style={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `5px solid ${label.color}` }} />
          </div>
        )}
      </div>
    )
  })
}

export default memo(IndicatorOverlayLabels)
